/**
 * SCP Image Zoom
 * Reusable zoom module for images with pan functionality
 *
 * Usage:
 * new SCPImageZoom('.my-zoomable-images', { zoomFactor: 2.5 });
 *
 * Options:
 * - zoomFactor: number (default: 2) - The zoom multiplier
 * - transitionDuration: number (default: 300) - Transition duration in ms
 * - easing: string (default: 'ease-out') - CSS easing function
 * - disableOnMobile: boolean (default: true) - Disable on touch devices
 */

class SCPImageZoom {
  constructor(selector, options = {}) {
    this.options = {
      zoomFactor: 2,
      transitionDuration: 300,
      easing: 'ease-out',
      disableOnMobile: false,
      ...options,
    };

    this.instances = [];
    this.init(selector);
  }

  init(selector) {
    const elements = document.querySelectorAll(selector);
    elements.forEach((element) => {
      this.initInstance(element);
    });
  }

  initInstance(container) {
    // Find the image inside the container
    const image = container.querySelector('img');
    if (!image) return;

    // Check if already initialized
    if (container.dataset.zoomInitialized) return;
    container.dataset.zoomInitialized = 'true';

    // Disable on touch devices if option is set
    if (this.options.disableOnMobile && this.isTouchDevice()) {
      return;
    }

    // Instance state
    const state = {
      isZoomed: false,
      container,
      image,
      originalTransform: '',
      originalSrc: image.src,
      originalSrcset: image.srcset || '',
      maxResSrc: this.getMaxResolutionSrc(image),
      bounds: null,
    };

    this.instances.push(state);

    // Setup container styles
    this.setupContainer(container);

    // Setup image styles
    this.setupImage(image);

    // Bind events
    this.bindEvents(state);
  }

  isTouchDevice() {
    return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  }

  /**
   * Parse srcset and return the URL of the highest resolution image
   */
  getMaxResolutionSrc(image) {
    const srcset = image.srcset;
    if (!srcset) {
      // No srcset, try to get max res from src by modifying width parameter
      return this.getMaxResFromSrc(image.src);
    }

    // Parse srcset: "url1 100w, url2 200w, url3 300w"
    const sources = srcset.split(',').map((source) => {
      const parts = source.trim().split(/\s+/);
      const url = parts[0];
      const descriptor = parts[1] || '';

      // Parse width descriptor (e.g., "1946w" -> 1946)
      let width = 0;
      if (descriptor.endsWith('w')) {
        width = parseInt(descriptor, 10) || 0;
      } else if (descriptor.endsWith('x')) {
        // For pixel density descriptors, estimate width
        width = parseFloat(descriptor) * 1000;
      }

      return { url, width };
    });

    // Find the source with the highest width
    const maxSource = sources.reduce((max, current) => (current.width > max.width ? current : max), {
      url: image.src,
      width: 0,
    });

    return maxSource.url;
  }

  /**
   * Try to get max resolution from Shopify CDN URL by modifying width param
   */
  getMaxResFromSrc(src) {
    // Shopify CDN URLs often have width parameter like: image.jpg?width=800
    // Try to increase it to max
    if (src.includes('cdn.shopify.com')) {
      // Remove width parameter and add max width
      const url = new URL(src);
      url.searchParams.set('width', '2048');
      return url.toString();
    }
    return src;
  }

  setupContainer(container) {
    container.style.overflow = 'hidden';
    container.style.position = 'relative';
  }

  setupImage(image) {
    image.style.transition = `transform ${this.options.transitionDuration}ms ${this.options.easing}`;
    image.style.transformOrigin = 'center center';
    image.style.willChange = 'transform';
    image.draggable = false;
  }

  bindEvents(state) {
    const { container, image } = state;

    // Click to toggle zoom
    container.addEventListener('click', (e) => {
      e.preventDefault();
      this.toggleZoom(state);
    });

    // Mouse move for panning
    container.addEventListener('mousemove', (e) => {
      if (state.isZoomed) {
        this.handlePan(state, e);
      }
    });

    // Mouse leave - exit zoom
    container.addEventListener('mouseleave', () => {
      if (state.isZoomed) {
        this.resetZoom(state);
      }
    });

    // Reset on window resize
    window.addEventListener('resize', () => {
      if (state.isZoomed) {
        this.resetZoom(state);
      }
    });
  }

  toggleZoom(state) {
    if (state.isZoomed) {
      this.resetZoom(state);
    } else {
      this.activateZoom(state);
    }
  }

  activateZoom(state) {
    const { container, image, maxResSrc } = state;
    state.isZoomed = true;

    // Calculate bounds for panning
    state.bounds = this.calculateBounds(state);

    // Switch to maximum resolution image
    if (maxResSrc && maxResSrc !== image.src) {
      // Preload the high-res image
      const preloader = new Image();
      preloader.onload = () => {
        // Only switch if still zoomed (user didn't exit while loading)
        if (state.isZoomed) {
          image.src = maxResSrc;
          // Remove srcset to ensure max res is used
          image.removeAttribute('srcset');
        }
      };
      preloader.src = maxResSrc;
    }

    // Apply zoom
    image.style.transform = `scale(${this.options.zoomFactor})`;

    // Add active class
    container.classList.add('scp-zoom-active');

    // Dispatch custom event
    container.dispatchEvent(new CustomEvent('zoomActivated', { detail: state }));
  }

  resetZoom(state) {
    const { container, image, originalSrc, originalSrcset } = state;
    state.isZoomed = false;

    // Reset transform
    image.style.transform = 'scale(1) translate(0, 0)';

    // Restore original image source
    if (originalSrcset) {
      image.srcset = originalSrcset;
    }
    image.src = originalSrc;

    // Remove active class
    container.classList.remove('scp-zoom-active');

    // Dispatch custom event
    container.dispatchEvent(new CustomEvent('zoomDeactivated', { detail: state }));
  }

  calculateBounds(state) {
    const { container, image } = state;
    const { zoomFactor } = this.options;

    const containerRect = container.getBoundingClientRect();
    const imageRect = image.getBoundingClientRect();

    // Calculate the zoomed dimensions
    const zoomedWidth = imageRect.width * zoomFactor;
    const zoomedHeight = imageRect.height * zoomFactor;

    // Calculate max translate values (half of the overflow on each side)
    const maxTranslateX = (zoomedWidth - containerRect.width) / 2 / zoomFactor;
    const maxTranslateY = (zoomedHeight - containerRect.height) / 2 / zoomFactor;

    return {
      maxX: Math.max(0, maxTranslateX),
      maxY: Math.max(0, maxTranslateY),
      containerWidth: containerRect.width,
      containerHeight: containerRect.height,
    };
  }

  handlePan(state, event) {
    const { container, image, bounds } = state;
    const { zoomFactor } = this.options;

    if (!bounds) return;

    const containerRect = container.getBoundingClientRect();

    // Calculate mouse position relative to container center (0 to 1, centered at 0.5)
    const mouseX = (event.clientX - containerRect.left) / containerRect.width;
    const mouseY = (event.clientY - containerRect.top) / containerRect.height;

    // Convert to -1 to 1 range (center is 0)
    const normalizedX = (mouseX - 0.5) * 2;
    const normalizedY = (mouseY - 0.5) * 2;

    // Calculate translation (inverted so image follows opposite direction of mouse)
    // This creates the "looking through a window" effect
    const translateX = -normalizedX * bounds.maxX;
    const translateY = -normalizedY * bounds.maxY;

    // Apply transform with smooth transition disabled for immediate response
    image.style.transition = 'none';
    image.style.transform = `scale(${zoomFactor}) translate(${translateX}px, ${translateY}px)`;

    // Re-enable transition after a frame
    requestAnimationFrame(() => {
      image.style.transition = `transform ${this.options.transitionDuration}ms ${this.options.easing}`;
    });
  }

  // Public method to destroy all instances
  destroy() {
    this.instances.forEach((state) => {
      const { container, image } = state;

      // Reset styles
      container.style.overflow = '';
      container.style.position = '';
      container.classList.remove('scp-zoom-active');

      image.style.transition = '';
      image.style.transform = '';
      image.style.transformOrigin = '';
      image.style.willChange = '';

      // Remove initialized flag
      delete container.dataset.zoomInitialized;
    });

    this.instances = [];
  }

  // Public method to get zoom state for a specific container
  isZoomed(container) {
    const instance = this.instances.find((s) => s.container === container);
    return instance ? instance.isZoomed : false;
  }
}

/**
 * SCP Mobile Zoom
 * Fullscreen zoom overlay for mobile with touch pan (same behavior as desktop)
 */
class SCPMobileZoom {
  constructor(selector, options = {}) {
    this.options = {
      zoomFactor: 2.8,
      ...options,
    };

    this.overlay = null;
    this.state = {
      isOpen: false,
      translateX: 0,
      translateY: 0,
      bounds: null,
    };

    this.init(selector);
  }

  init(selector) {
    // Only init on touch devices
    if (!this.isTouchDevice()) return;

    this.createOverlay();
    this.bindContainerEvents(selector);
  }

  isTouchDevice() {
    return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  }

  createOverlay() {
    this.overlay = document.createElement('div');
    this.overlay.className = 'scp-mobile-zoom-overlay';
    this.overlay.innerHTML = `
     <button id="ModalClose-template--25147715748175__main" type="button" class="product-media-modal__toggle" aria-label="Fermer">
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" class="icon icon-close" viewBox="0 0 18 17"><path fill="currentColor" d="M.865 15.978a.5.5 0 0 0 .707.707l7.433-7.431 7.579 7.282a.501.501 0 0 0 .846-.37.5.5 0 0 0-.153-.351L9.712 8.546l7.417-7.416a.5.5 0 1 0-.707-.708L8.991 7.853 1.413.573a.5.5 0 1 0-.693.72l7.563 7.268z"></path></svg>

    </button>
      <div class="scp-mobile-zoom-container">
        <img class="scp-mobile-zoom-image" src="" alt="">
      </div>
    `;
    this.overlay.style.display = 'none';
    document.body.appendChild(this.overlay);

    this.closeBtn = this.overlay.querySelector('.product-media-modal__toggle');
    this.zoomContainer = this.overlay.querySelector('.scp-mobile-zoom-container');
    this.zoomImage = this.overlay.querySelector('.scp-mobile-zoom-image');

    // Close events
    this.closeBtn.addEventListener('click', () => this.close());

    // Touch pan events
    this.bindTouchEvents();
  }

  bindContainerEvents(selector) {
    document.querySelectorAll(selector).forEach((container) => {
      const img = container.querySelector('img');
      if (!img) return;

      container.addEventListener('click', (e) => {
        e.preventDefault();
        this.open(img);
      });
    });
  }

  open(sourceImg) {
    const highResSrc = this.getMaxResolutionSrc(sourceImg);

    this.zoomImage.src = highResSrc;
    this.zoomImage.alt = sourceImg.alt || '';

    // Reset state
    this.state.translateX = 0;
    this.state.translateY = 0;
    this.state.isOpen = true;

    // Show overlay
    this.overlay.style.display = 'flex';
    document.body.style.overflow = 'hidden';

    // Wait for image to load then calculate bounds and apply initial zoom
    this.zoomImage.onload = () => {
      this.calculateBounds();
      this.applyTransform();
    };

    // If already loaded (cached)
    if (this.zoomImage.complete) {
      this.calculateBounds();
      this.applyTransform();
    }
  }

  close() {
    this.overlay.style.display = 'none';
    document.body.style.overflow = '';
    this.state.isOpen = false;
    this.zoomImage.src = '';
  }

  getMaxResolutionSrc(image) {
    const srcset = image.srcset;
    if (!srcset) {
      if (image.src.includes('cdn.shopify.com')) {
        const url = new URL(image.src);
        url.searchParams.set('width', '2048');
        return url.toString();
      }
      return image.src;
    }

    const sources = srcset.split(',').map((source) => {
      const parts = source.trim().split(/\s+/);
      const url = parts[0];
      const descriptor = parts[1] || '';
      let width = 0;
      if (descriptor.endsWith('w')) {
        width = parseInt(descriptor, 10) || 0;
      }
      return { url, width };
    });

    const maxSource = sources.reduce((max, current) => (current.width > max.width ? current : max), {
      url: image.src,
      width: 0,
    });

    return maxSource.url;
  }

  calculateBounds() {
    const containerRect = this.zoomContainer.getBoundingClientRect();
    const imageRect = this.zoomImage.getBoundingClientRect();
    const { zoomFactor } = this.options;

    // Calculate zoomed dimensions
    const zoomedWidth = imageRect.width * zoomFactor;
    const zoomedHeight = imageRect.height * zoomFactor;

    // Calculate max translate values
    const maxTranslateX = (zoomedWidth - containerRect.width) / 2 / zoomFactor;
    const maxTranslateY = (zoomedHeight - containerRect.height) / 2 / zoomFactor;

    this.state.bounds = {
      maxX: Math.max(0, maxTranslateX),
      maxY: Math.max(0, maxTranslateY),
      containerWidth: containerRect.width,
      containerHeight: containerRect.height,
    };
  }

  bindTouchEvents() {
    let startX = 0;
    let startY = 0;
    let startTranslateX = 0;
    let startTranslateY = 0;

    this.zoomContainer.addEventListener(
      'touchstart',
      (e) => {
        if (e.touches.length === 1) {
          startX = e.touches[0].clientX;
          startY = e.touches[0].clientY;
          startTranslateX = this.state.translateX;
          startTranslateY = this.state.translateY;
          this.zoomImage.style.transition = 'none';
        }
      },
      { passive: true },
    );

    this.zoomContainer.addEventListener(
      'touchmove',
      (e) => {
        if (e.touches.length === 1 && this.state.bounds) {
          e.preventDefault();

          const deltaX = e.touches[0].clientX - startX;
          const deltaY = e.touches[0].clientY - startY;

          // Calculate new translation (divide by zoomFactor for proper scaling)
          let newTranslateX = startTranslateX + deltaX / this.options.zoomFactor;
          let newTranslateY = startTranslateY + deltaY / this.options.zoomFactor;

          // Clamp to bounds
          newTranslateX = Math.max(-this.state.bounds.maxX, Math.min(this.state.bounds.maxX, newTranslateX));
          newTranslateY = Math.max(-this.state.bounds.maxY, Math.min(this.state.bounds.maxY, newTranslateY));

          this.state.translateX = newTranslateX;
          this.state.translateY = newTranslateY;
          this.applyTransform();
        }
      },
      { passive: false },
    );

    this.zoomContainer.addEventListener(
      'touchend',
      () => {
        this.zoomImage.style.transition = 'transform 0.2s ease-out';
      },
      { passive: true },
    );
  }

  applyTransform() {
    const { zoomFactor } = this.options;
    this.zoomImage.style.transform = `scale(${zoomFactor}) translate(${this.state.translateX}px, ${this.state.translateY}px)`;
  }
}

// Auto-initialize if data attribute is present
document.addEventListener('DOMContentLoaded', () => {
  const autoInitContainers = document.querySelectorAll('[data-scp-zoom]');
  if (autoInitContainers.length > 0) {
    // Desktop zoom (hover/click with mouse pan)
    new SCPImageZoom('[data-scp-zoom]', {
      zoomFactor: 2.8,
      transitionDuration: 300,
      disableOnMobile: true,
    });

    // Mobile fullscreen zoom (touch pan)
    new SCPMobileZoom('[data-scp-zoom]', {
      zoomFactor: 2.8,
    });
  }
});

// Export for module usage
if (typeof module !== 'undefined' && module.exports) {
  module.exports = { SCPImageZoom, SCPMobileZoom };
}
